import React, { useState, useMemo, useCallback, useEffect, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePaymentReturnResume } from "../../common/hooks/usePaymentReturnResume";
import "./Summary.scss";
import {
  updateJourneyStage,
  saveSummaryStageData,
  JOURNEY_STAGE,
  PAYMENT_FLOW,
} from "../../api/journeyService";
import { updateQuoteSummary } from "../../api/quoteService";
import { resolveBiApiProductId } from "../../api/paymentService";
import { logError } from "../../common/utils/logger";
import { calculateAge, formatCurrency } from "../../common/utils/formatters";
import { getLeadDateOfBirth } from "../../common/utils/journeyLeadFields";
import { useJourney } from "../../common/hooks/useJourney";
import NeedhelpModal from "../../common/components/NeedhelpModal";
import Modal from "../../common/components/Modal";
import BrandLoader from "../../common/components/BrandLoader";
import { journeyActions } from "../../store/journeySlice";
import { useDispatch, useSelector, useStore } from "react-redux";
import { slimJourneyReduxSnapshotForPersistence } from "../../common/utils/journeyPersistenceSlim";
import { redirectToLatestJourneyStage } from "../../common/utils/journeyStageGuard";
import { TEXT_CONTENT, API_BASE_URL } from "../../constants";

const SUMMARY_BI_GUIDED_HINT_TEXT = "Download Benefits Illustration PDF";

const GuidedHintArrow = ({ direction = "down" }) => (
  <span
    className={`quote-guided-hint__arrow quote-guided-hint__arrow--${direction}`}
    aria-hidden
  >
    <svg
      width="28"
      height="28"
      viewBox="0 0 28 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#summary-guided-hint-arrow-clip)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M13.2938 0.291834C13.3867 0.198708 13.497 0.124822 13.6185 0.0744094C13.74 0.0239965 13.8703 -0.00195313 14.0018 -0.00195312C14.1333 -0.00195313 14.2636 0.0239965 14.3851 0.0744094C14.5065 0.124822 14.6169 0.198708 14.7098 0.291834L21.7098 7.29183C21.85 7.43169 21.9455 7.61004 21.9843 7.80425C22.023 7.99847 22.0032 8.19981 21.9274 8.38276C21.8515 8.56571 21.7231 8.72202 21.5583 8.83187C21.3935 8.94173 21.1998 9.00019 21.0018 8.99983H16.0018V25.9998C16.0018 26.5303 15.7911 27.039 15.416 27.414C15.0409 27.7891 14.5322 27.9998 14.0018 27.9998C13.4714 27.9998 12.9626 27.7891 12.5876 27.414C12.2125 27.039 12.0018 26.5303 12.0018 25.9998V8.99983H7.00179C6.80375 9.00019 6.61006 8.94173 6.44528 8.83187C6.2805 8.72202 6.15206 8.56571 6.07622 8.38276C6.00039 8.19981 5.98058 7.99847 6.01931 7.80425C6.05805 7.61004 6.15358 7.43169 6.29379 7.29183L13.2938 0.291834Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="summary-guided-hint-arrow-clip">
          <rect width="28" height="28" fill="white" />
        </clipPath>
      </defs>
    </svg>
  </span>
);

const cloneForJourney = (value) => {
  if (value == null) return value;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return null;
  }
};

const getDisplayFrequencyLabel = (frequency) => {
  const normalized = String(frequency || "Yearly")
    .trim()
    .toLowerCase();
  if (normalized === "monthly") return "Month";
  if (normalized === "quarterly" || normalized === "quaterly") return "Quarter";
  if (normalized === "half-yearly" || normalized === "half yearly")
    return "Half Year";
  return "Year";
};

const PhonePeIcon = () => (
  <svg
    width="15"
    height="14"
    viewBox="0 0 15 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clip-path="url(#clip0_33821_37335)">
      <path
        d="M9.22047 0.189222C7.77733 -0.128809 6.26555 -0.0402998 4.8763 0.443558C3.48705 0.927415 2.28272 1.78489 1.41559 2.90756C0.548469 4.03022 0.0574922 5.36766 0.00474637 6.75076C-0.0479995 8.13385 0.339855 9.50049 1.11927 10.6779C1.89868 11.8552 3.03464 12.7904 4.38352 13.3653C5.73239 13.9401 7.2336 14.1287 8.69732 13.9072C10.161 13.6858 11.5215 13.0642 12.6068 12.1211C13.692 11.178 14.4532 9.9557 14.7942 8.60882C15.0206 7.7144 15.0561 6.78711 14.8985 5.8799C14.7409 4.9727 14.3934 4.10335 13.8759 3.3215C13.3583 2.53965 12.6808 1.86061 11.882 1.32317C11.0832 0.785734 10.1788 0.400415 9.22047 0.189222Z"
        fill="#5F259F"
      />
      <path
        d="M10.9006 5.17421C10.8966 5.04117 10.8377 4.91471 10.7363 4.82113C10.6349 4.72755 10.4988 4.67406 10.3562 4.67177H9.35035L7.04688 2.20285C6.93913 2.08985 6.79917 2.00779 6.64333 1.96623C6.48749 1.92468 6.3222 1.92534 6.16675 1.96815L5.36841 2.20285C5.34004 2.21183 5.31443 2.22708 5.29375 2.24731C5.27307 2.26754 5.25795 2.29214 5.24967 2.31901C5.24139 2.34588 5.24021 2.37422 5.24622 2.40161C5.25223 2.429 5.26526 2.45462 5.28419 2.47629L7.80005 4.70481H3.98902C3.96134 4.70366 3.9337 4.70793 3.90789 4.71733C3.88208 4.72673 3.85865 4.74106 3.83912 4.7594C3.81958 4.77774 3.80436 4.79969 3.79444 4.82384C3.78452 4.84799 3.78011 4.8738 3.7815 4.89963V5.29042C3.78401 5.42445 3.84217 5.55234 3.94374 5.64714C4.0453 5.74193 4.18232 5.79621 4.32593 5.79856H4.91309V7.67616C4.91309 9.08322 5.70899 9.90468 7.04932 9.90468C7.4507 9.91618 7.85023 9.84944 8.22242 9.70871V10.962C8.22127 11.0444 8.23782 11.1262 8.27109 11.2026C8.30436 11.279 8.35369 11.3483 8.41615 11.4066C8.47861 11.4649 8.55294 11.511 8.63477 11.542C8.7166 11.5731 8.80426 11.5885 8.89258 11.5875H9.47852C9.54371 11.5832 9.60506 11.5571 9.65127 11.514C9.69748 11.4709 9.72543 11.4136 9.72999 11.3528V5.76096H10.6907C10.7187 5.76245 10.7467 5.75839 10.7729 5.74907C10.7991 5.73975 10.8229 5.72537 10.8427 5.70687C10.8625 5.68836 10.8779 5.66616 10.8879 5.64171C10.8979 5.61726 10.9022 5.59112 10.9006 5.56499V5.17421ZM8.21509 8.53635C7.95088 8.64421 7.6648 8.69746 7.37647 8.69244C6.7063 8.69244 6.37183 8.38026 6.37183 7.67616V5.7997H8.21509V8.53407V8.53635Z"
        fill="white"
      />
    </g>
    <defs>
      <clipPath id="clip0_33821_37335">
        <rect width="15" height="14" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const GooglePayIcon = () => (
  <svg
    width="17"
    height="14"
    viewBox="0 0 17 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clip-path="url(#clip0_33821_37338)">
      <path
        d="M15.4362 2.70842C13.7594 1.77236 11.6153 2.32859 10.6463 3.95016L8.20374 8.04211C7.49696 9.22373 8.40683 9.63662 9.4214 10.2249L11.7717 11.5368C12.5677 11.9808 13.5843 11.7172 14.0434 10.9485L16.5544 6.74327C17.3979 5.33017 16.8974 3.52423 15.4362 2.70842Z"
        fill="#EA4335"
      />
      <path
        d="M12.6796 4.40068L10.3292 3.08881C9.03177 2.39229 8.29703 2.34618 7.76433 3.16498L4.29789 8.97074C3.32996 10.5913 3.90616 12.6638 5.58187 13.5979C7.04307 14.4137 8.91053 13.9296 9.75408 12.5165L13.2869 6.5985C13.748 5.82883 13.4755 4.84466 12.6796 4.40068Z"
        fill="#FBBC04"
      />
      <path
        d="M13.1249 1.41725L11.474 0.495233C9.64804 -0.524005 7.31323 0.0803207 6.2593 1.84619L3.11615 7.11074C2.65188 7.88745 2.92754 8.88162 3.7307 9.32961L5.57949 10.3619C6.49246 10.872 7.65934 10.5693 8.18684 9.68639L11.7777 3.67221C12.5217 2.42647 14.1684 1.99953 15.4566 2.71911L13.1249 1.41725Z"
        fill="#34A853"
      />
      <path
        d="M6.70924 3.352L4.92472 2.35782C4.12881 1.91485 3.1122 2.17742 2.65311 2.94511L0.512091 6.52298C-0.541843 8.28382 0.0830552 10.5368 1.90904 11.553L3.26765 12.3097L4.91538 13.2277L5.63045 13.6255C4.36095 12.8038 3.95783 11.1571 4.73402 9.86028L5.28845 8.93425L7.31859 5.5418C7.77665 4.77614 7.5041 3.79497 6.70924 3.352Z"
        fill="#4285F4"
      />
    </g>
    <defs>
      <clipPath id="clip0_33821_37338">
        <rect width="17" height="14" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const ApplePayIcon = () => (
  <svg
    width="21"
    height="14"
    viewBox="0 0 21 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.8371 4.55289C3.59097 4.82873 3.19718 5.04631 2.80338 5.01522C2.75416 4.64225 2.94696 4.24596 3.17257 4.00119C3.41869 3.71757 3.8494 3.51554 4.19808 3.5C4.2391 3.88852 4.07912 4.26927 3.8371 4.55289ZM4.19397 5.08904C3.62379 5.05796 3.13565 5.39597 2.86491 5.39597C2.59008 5.39597 2.17577 5.10458 1.72455 5.11235C1.13796 5.12012 0.592389 5.43482 0.29294 5.93601C-0.322365 6.93839 0.132961 8.42254 0.727756 9.23843C1.019 9.64249 1.36767 10.0854 1.8271 10.0699C2.26192 10.0543 2.4342 9.80178 2.95926 9.80178C3.48842 9.80178 3.6402 10.0699 4.09963 10.0621C4.57546 10.0543 4.87491 9.65803 5.16615 9.25397C5.49842 8.79551 5.63379 8.34872 5.64199 8.32541C5.63379 8.31764 4.72313 7.98739 4.71493 6.99279C4.70673 6.16136 5.43279 5.76507 5.4656 5.74175C5.0554 5.16675 4.41548 5.10458 4.19397 5.08904ZM7.48791 3.96234V10.0193H8.4806V7.94854H9.85478C11.11 7.94854 11.9919 7.13265 11.9919 5.95155C11.9919 4.77046 11.1264 3.96234 9.88759 3.96234H7.48791ZM8.4806 4.75492H9.62506C10.4865 4.75492 10.9787 5.19006 10.9787 5.95544C10.9787 6.72082 10.4865 7.15985 9.62096 7.15985H8.4806V4.75492ZM13.805 10.066C14.4285 10.066 15.0069 9.76681 15.2695 9.29282H15.29V10.0193H16.2088V7.00444C16.2088 6.13027 15.4705 5.56692 14.3342 5.56692C13.28 5.56692 12.5006 6.13804 12.4719 6.92285H13.3661C13.44 6.54987 13.805 6.30511 14.3055 6.30511C14.9126 6.30511 15.2531 6.57319 15.2531 7.0666V7.40073L14.0142 7.47066C12.8616 7.53671 12.2381 7.98351 12.2381 8.76055C12.2381 9.54536 12.8821 10.066 13.805 10.066ZM14.0717 9.34721C13.5425 9.34721 13.2061 9.10633 13.2061 8.73724C13.2061 8.35649 13.5302 8.13503 14.1496 8.10007L15.2531 8.03402V8.37591C15.2531 8.94315 14.7444 9.34721 14.0717 9.34721ZM17.4353 11.6667C18.4034 11.6667 18.8587 11.317 19.2566 10.2563L21 5.6252H19.9909L18.8218 9.20346H18.8013L17.6322 5.6252H16.5944L18.2762 10.0349L18.186 10.303C18.0342 10.7575 17.7881 10.9324 17.3492 10.9324C17.2713 10.9324 17.1195 10.9246 17.0579 10.9168V11.6434C17.1154 11.6589 17.3615 11.6667 17.4353 11.6667Z"
      fill="black"
    />
  </svg>
);

const VisaIcon = () => (
  <svg
    width="47"
    height="14"
    viewBox="0 0 47 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clip-path="url(#clip0_33821_37346)">
      <path
        d="M20.3725 13.7651H16.5391L18.9374 0.242188H22.7708L20.3725 13.7651Z"
        fill="#005CA8"
      />
      <path
        d="M13.3149 0.242188L9.65743 9.54127L9.22572 7.5376L7.9346 1.46788C7.9346 1.46788 7.77871 0.242188 6.11584 0.242188H0.071951L0 0.46971C1.41065 0.795752 2.75867 1.318 3.99728 2.01833L7.32701 13.7615H11.3243L17.4401 0.242188H13.3149Z"
        fill="#005CA8"
      />
      <path
        d="M43.4805 13.766H47.0021L43.9322 0.243073H40.8503C40.4766 0.216618 40.1035 0.300193 39.785 0.481665C39.4666 0.663137 39.2195 0.933061 39.0795 1.25225L33.3594 13.766H37.3567L38.1561 11.7587H43.0328L43.4805 13.766ZM39.2634 8.99536L41.262 3.93115L42.3932 8.99536H39.2634Z"
        fill="#005CA8"
      />
      <path
        d="M33.6549 3.49358L34.2026 0.590826C33.0942 0.226728 31.9297 0.0272802 30.7529 0C28.8502 0 24.3293 0.763303 24.3293 4.47706C24.3293 7.97064 29.6337 8.01468 29.6337 9.84954C29.6337 11.6844 24.8769 11.3578 23.306 10.1982L22.7344 13.233C24.1053 13.7615 25.5808 14.0226 27.0674 14C29.6816 14 33.631 12.756 33.631 9.36881C33.631 5.85321 28.2786 5.5266 28.2786 3.99633C28.2786 2.46605 32.0121 2.66422 33.6549 3.49358Z"
        fill="#005CA8"
      />
      <path
        d="M9.22572 7.54127L7.9346 1.46788C7.9346 1.46788 7.77871 0.242188 6.11584 0.242188H0.071951L0 0.46971C2.0814 0.956949 4.02325 1.85207 5.69213 3.09356C7.27672 4.26402 8.49583 5.7985 9.22572 7.54127Z"
        fill="#F6A723"
      />
    </g>
    <defs>
      <clipPath id="clip0_33821_37346">
        <rect width="47" height="14" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const UPIIcon = () => (
  <svg
    width="42"
    height="14"
    viewBox="0 0 42 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clip-path="url(#clip0_33821_37354)">
      <path
        d="M0.33388 12.0234H0.651056L0.367073 13.175C0.337665 13.2368 0.322461 13.3038 0.322461 13.3716C0.322461 13.4394 0.337665 13.5063 0.367073 13.5682C0.406203 13.6145 0.457231 13.6507 0.515319 13.6732C0.573408 13.6957 0.636633 13.7039 0.699001 13.6969C0.846393 13.7024 0.990783 13.6566 1.10469 13.5682C1.22069 13.4653 1.29832 13.3299 1.32598 13.182L1.62102 12.0304H1.9382L1.64684 13.2203C1.60355 13.4439 1.4798 13.6467 1.29647 13.7943C1.11043 13.9305 0.878934 13.9996 0.64368 13.9891C0.542317 14.0008 0.439462 13.9899 0.343381 13.9573C0.247301 13.9247 0.160675 13.8712 0.090466 13.8013C0.0372191 13.714 0.00469654 13.6168 -0.00485486 13.5165C-0.0144063 13.4162 -0.000758815 13.3151 0.0351447 13.2203L0.33388 12.0234Z"
        fill="#3D3D3C"
      />
      <path
        d="M1.90625 13.916L2.40414 11.9434L3.35567 13.1019L3.42943 13.2063C3.45525 13.2411 3.47738 13.2863 3.51057 13.3315L3.8425 12.0199H4.13755L3.63965 13.9891L2.66969 12.8097L2.58486 12.7088C2.56012 12.6742 2.54028 12.6368 2.52585 12.5974L2.21605 13.916H1.921H1.90625Z"
        fill="#3D3D3C"
      />
      <path
        d="M4.10938 13.9176L4.58883 12.0215H4.90969L4.42655 13.9176H4.10938Z"
        fill="#3D3D3C"
      />
      <path
        d="M4.875 13.9176L5.35445 12.0215H6.46088L6.39818 12.2824H5.62368L5.50198 12.7556H6.26172L6.19165 13.0235H5.41715L5.19586 13.9176H4.875Z"
        fill="#3D3D3C"
      />
      <path
        d="M6.32812 13.9176L6.80758 12.0215H7.12844L6.63792 13.9176H6.32812Z"
        fill="#3D3D3C"
      />
      <path
        d="M7.10156 13.9157L7.58101 12.0195H8.68744L8.62474 12.2805H7.85025L7.72854 12.7571H8.48091L8.41083 13.025H7.6474L7.4925 13.6373H8.26331L8.19324 13.9157H7.10156Z"
        fill="#3D3D3C"
      />
      <path
        d="M8.5625 13.9174L9.04195 12.0213H9.47346C9.66753 12.0143 9.86181 12.0283 10.0525 12.063C10.1507 12.0862 10.2409 12.1329 10.3143 12.1987C10.4071 12.2855 10.4689 12.3974 10.4914 12.5188C10.5133 12.6686 10.5045 12.8209 10.4656 12.9676C10.425 13.1252 10.3578 13.2756 10.2664 13.4129C10.1776 13.5403 10.0621 13.6492 9.92709 13.733C9.82757 13.7971 9.71625 13.8431 9.59885 13.8687C9.42776 13.9012 9.25329 13.9152 9.07883 13.9104H8.5625V13.9174ZM8.95344 13.6356H9.21898C9.33281 13.6377 9.44656 13.6284 9.55829 13.6078C9.63568 13.5889 9.70837 13.5558 9.77219 13.5103C9.86468 13.4509 9.94161 13.3724 9.99717 13.2807C10.0608 13.1817 10.1069 13.0736 10.1336 12.9606C10.1619 12.8585 10.1694 12.7523 10.1558 12.6475C10.1448 12.5617 10.1077 12.4807 10.0488 12.4144C10.0003 12.3681 9.93894 12.3356 9.87177 12.3205C9.75044 12.2975 9.62664 12.2881 9.50296 12.2927H9.29274L8.95344 13.6356Z"
        fill="#3D3D3C"
      />
      <path
        d="M11.9313 13.0675L11.7174 13.9164H11.4297L11.9091 12.0202H12.396C12.4985 12.0171 12.6011 12.0241 12.7021 12.0411C12.761 12.0506 12.8152 12.0774 12.857 12.1177C12.9106 12.1644 12.9457 12.227 12.9566 12.2951C12.9729 12.3767 12.9729 12.4605 12.9566 12.5421C12.9323 12.6306 12.8937 12.7151 12.8422 12.7926C12.7908 12.8627 12.7257 12.9229 12.6504 12.97C12.589 13.0033 12.5233 13.029 12.455 13.0466C12.3502 13.063 12.244 13.07 12.1378 13.0675H11.9534H11.9313ZM12.0161 12.8135H12.0935C12.2194 12.8221 12.3456 12.8031 12.4624 12.7578C12.4983 12.7354 12.529 12.7062 12.5525 12.6721C12.576 12.638 12.5917 12.5996 12.5988 12.5595C12.6141 12.5244 12.6187 12.4859 12.6122 12.4485C12.6057 12.411 12.5883 12.376 12.5619 12.3473C12.4605 12.2953 12.3448 12.2734 12.23 12.2847H12.1673L12.0161 12.8135Z"
        fill="#3D3D3C"
      />
      <path
        d="M14.0123 13.916L13.9127 13.4255H13.1382L12.7694 13.916H12.4375L13.8832 11.9434L14.3516 13.916H14.0123ZM13.3263 13.168H13.8685L13.78 12.7331C13.776 12.7019 13.776 12.6704 13.78 12.6392C13.78 12.6009 13.78 12.5661 13.78 12.5209C13.7608 12.5618 13.7387 12.6013 13.7136 12.6392C13.6963 12.674 13.6766 12.7077 13.6546 12.7401L13.3263 13.168Z"
        fill="#3D3D3C"
      />
      <path
        d="M14.8098 13.9159L15.0311 13.0496L14.6328 12.0059H14.9647L15.2155 12.6495C15.2155 12.6704 15.2155 12.6878 15.234 12.7156C15.2524 12.7434 15.234 12.7747 15.2598 12.8026C15.2819 12.7747 15.3004 12.7434 15.3188 12.7156C15.3379 12.6899 15.3588 12.6655 15.3815 12.6425L15.9642 12.0059H16.2851L15.352 13.0287L15.1196 13.9159H14.7988H14.8098Z"
        fill="#3D3D3C"
      />
      <path
        d="M17.8492 12.9832C17.8492 12.9832 17.8492 12.924 17.8492 12.8579C17.8492 12.7918 17.8492 12.7605 17.8676 12.7223C17.8455 12.764 17.8271 12.8092 17.8013 12.8545C17.7744 12.9001 17.7448 12.9442 17.7127 12.9867L16.9641 13.9991L16.717 12.9554C16.7055 12.9142 16.6969 12.8723 16.6911 12.8301C16.6875 12.7919 16.6875 12.7535 16.6911 12.7153L16.6321 12.8719C16.6166 12.9186 16.5969 12.964 16.5731 13.0075L16.1232 13.9156H15.8281L16.7944 11.9395L17.0673 13.1363C17.0696 13.1675 17.0696 13.1989 17.0673 13.2302C17.0673 13.272 17.0673 13.3276 17.0931 13.3937C17.1263 13.3381 17.1706 13.272 17.2333 13.185C17.2466 13.1702 17.2577 13.1538 17.2665 13.1363L18.1369 11.9395L18.1184 13.9156H17.8271L17.8492 12.9832Z"
        fill="#3D3D3C"
      />
      <path
        d="M18.375 13.9176L18.8545 12.0215H19.9609L19.8945 12.2824H19.1089L18.9872 12.7591H19.7617L19.6916 13.027H18.9172L18.7622 13.6393H19.5404L19.4704 13.9176H18.375Z"
        fill="#3D3D3C"
      />
      <path
        d="M19.8359 13.916L20.3338 11.9434L21.2817 13.1019C21.3063 13.1321 21.3321 13.1669 21.3591 13.2063L21.4366 13.3315L21.7574 12.0234H22.0525L21.5509 13.9891L20.5846 12.8097C20.5588 12.7749 20.533 12.7436 20.5109 12.7088C20.485 12.6744 20.4639 12.6369 20.4482 12.5974L20.131 13.916H19.8359Z"
        fill="#3D3D3C"
      />
      <path
        d="M23.0831 12.2824L22.6663 13.9176H22.3491L22.7622 12.2859H22.2422L22.3086 12.0215H23.6658L23.5994 12.2824H23.0831Z"
        fill="#3D3D3C"
      />
      <path
        d="M23.4153 13.5375L23.7029 13.4261C23.7013 13.462 23.708 13.4977 23.7227 13.5308C23.7373 13.5638 23.7596 13.5935 23.7878 13.6175C23.8532 13.6644 23.9343 13.6878 24.0164 13.6836C24.1039 13.6851 24.1894 13.6594 24.2598 13.6105C24.3299 13.5597 24.3783 13.4869 24.3963 13.4053C24.4258 13.2939 24.3557 13.193 24.1861 13.1095C24.1688 13.0983 24.1502 13.089 24.1307 13.0817C23.9851 13.0195 23.8582 12.9238 23.7619 12.8034C23.7124 12.7107 23.7018 12.6038 23.7324 12.5042C23.77 12.3528 23.8627 12.2186 23.9943 12.1249C24.127 12.0277 24.291 11.9762 24.459 11.9788C24.5789 11.9752 24.6976 12.0029 24.802 12.0588C24.8429 12.0851 24.877 12.1197 24.9019 12.16C24.9268 12.2004 24.9418 12.2454 24.9458 12.2919L24.6655 12.4137C24.6492 12.3621 24.6171 12.3161 24.5733 12.2815C24.5289 12.2529 24.4759 12.2382 24.4221 12.2398C24.344 12.2395 24.2678 12.2626 24.2045 12.3059C24.1416 12.3468 24.098 12.4091 24.0828 12.4798C24.0533 12.5911 24.1381 12.699 24.3336 12.7999L24.3668 12.8173C24.4997 12.8737 24.6152 12.9611 24.7024 13.0713C24.7526 13.1687 24.7631 13.2801 24.7319 13.3844C24.6924 13.5537 24.5921 13.7048 24.4479 13.8123C24.2993 13.9181 24.1177 13.9744 23.9316 13.9724C23.7981 13.9765 23.6674 13.9358 23.5628 13.8576C23.5123 13.8196 23.4725 13.7706 23.4468 13.7148C23.4211 13.659 23.4102 13.5981 23.4153 13.5375Z"
        fill="#3D3D3C"
      />
      <path
        d="M25.7656 13.9157L26.2451 12.0195H26.5475L26.0828 13.9157H25.7656Z"
        fill="#3D3D3C"
      />
      <path
        d="M26.5547 13.916L27.0526 11.9434L28.0004 13.1019C28.028 13.1348 28.0526 13.1697 28.0742 13.2063C28.1 13.2411 28.1258 13.2863 28.1553 13.3315L28.4909 12.0199H28.7675L28.266 13.9856L27.2997 12.8062L27.2222 12.7053C27.1989 12.67 27.1792 12.6327 27.1632 12.594L26.846 13.916H26.5547Z"
        fill="#3D3D3C"
      />
      <path
        d="M29.794 12.2805L29.3773 13.9157H29.0601L29.4731 12.2839H28.9531L29.0195 12.0195H30.3767L30.3103 12.2839L29.794 12.2805Z"
        fill="#3D3D3C"
      />
      <path
        d="M30.1406 13.9161L30.609 12.0234H31.7154L31.6527 12.2844H30.8782L30.7602 12.761H31.531L31.461 13.0289H30.6902L30.5353 13.6412H31.3061L31.2397 13.9196L30.1406 13.9161Z"
        fill="#3D3D3C"
      />
      <path
        d="M32.1064 13.0666L31.8925 13.9155H31.5938L32.0806 12.0159H32.5231C32.6221 12.0141 32.721 12.0222 32.8182 12.0402C32.8806 12.051 32.9383 12.0788 32.9842 12.1202C33.0324 12.1697 33.0645 12.2314 33.0764 12.2977C33.0946 12.3768 33.0946 12.4586 33.0764 12.5377C33.045 12.6702 32.9732 12.7912 32.8698 12.8856C32.7666 12.972 32.638 13.0266 32.501 13.0422L32.9362 13.9155H32.5674L32.1322 13.07L32.1064 13.0666ZM32.1875 12.8265H32.2465C32.3725 12.8381 32.4995 12.8189 32.6154 12.7708C32.6512 12.7478 32.6816 12.718 32.7044 12.6832C32.7273 12.6485 32.7422 12.6096 32.7481 12.569C32.7739 12.4647 32.7481 12.3881 32.7149 12.3464C32.6208 12.2928 32.5108 12.2696 32.4014 12.2803H32.3424L32.1875 12.8265Z"
        fill="#3D3D3C"
      />
      <path
        d="M33.1406 13.9176L33.6201 12.0215H34.7265L34.6601 12.2824H33.8746L33.7528 12.7556H34.5273L34.4573 13.0235H33.6828L33.4578 13.9176H33.1406Z"
        fill="#3D3D3C"
      />
      <path
        d="M35.7697 13.916L35.6701 13.4255H34.8882L34.5194 13.916H34.1875L35.6332 11.9434L36.1016 13.916H35.7697ZM35.0763 13.168H35.6185L35.53 12.7331C35.5277 12.7018 35.5277 12.6705 35.53 12.6392C35.53 12.6009 35.53 12.5661 35.53 12.5209C35.5123 12.5624 35.4901 12.6021 35.4636 12.6392C35.4477 12.6747 35.428 12.7085 35.4046 12.7401L35.0763 13.168Z"
        fill="#3D3D3C"
      />
      <path
        d="M38.1805 12.4827C38.1134 12.4101 38.0286 12.354 37.9334 12.3192C37.8381 12.2801 37.7349 12.2611 37.631 12.2635C37.4209 12.2629 37.2173 12.3318 37.0556 12.4584C36.8861 12.5908 36.769 12.7735 36.7237 12.9768C36.695 13.0609 36.6861 13.1499 36.6976 13.2376C36.709 13.3253 36.7406 13.4097 36.7901 13.4847C36.8403 13.551 36.9077 13.604 36.9858 13.6387C37.0639 13.6734 37.1502 13.6887 37.2364 13.683C37.3536 13.6836 37.4699 13.6636 37.5793 13.6239C37.7041 13.5785 37.8219 13.5176 37.9297 13.443L37.8412 13.7909C37.7439 13.8511 37.6368 13.8958 37.524 13.9231C37.4135 13.9515 37.2993 13.9656 37.1847 13.9648C37.0482 13.9683 36.9125 13.9446 36.7864 13.8952C36.6731 13.8501 36.5749 13.7767 36.5024 13.683C36.4279 13.5892 36.3796 13.4791 36.3623 13.3629C36.3419 13.2316 36.3507 13.0977 36.3881 12.9698C36.4227 12.8307 36.4813 12.6979 36.5614 12.5767C36.6433 12.4542 36.7468 12.3459 36.8675 12.2566C36.9768 12.1666 37.1016 12.0949 37.2364 12.0443C37.3726 11.999 37.516 11.9755 37.6605 11.9748C37.7697 11.9728 37.8783 11.9893 37.9813 12.0235C38.079 12.0557 38.1691 12.1053 38.2469 12.1696L38.1805 12.4827Z"
        fill="#3D3D3C"
      />
      <path
        d="M38.2109 13.9176L38.6904 12.0215H39.7968L39.7304 12.2824H38.9449L38.8232 12.7591H39.5977L39.5276 13.027H38.7531L38.5982 13.6393H39.3727L39.3026 13.9176H38.2109Z"
        fill="#3D3D3C"
      />
      <path
        d="M32.4017 10.3713H30.2109L33.2536 0H35.4443L32.4017 10.3713Z"
        fill="#70706E"
      />
      <path
        d="M31.2583 0.323233C31.1767 0.22297 31.0699 0.143442 30.9476 0.091817C30.8253 0.0401917 30.6913 0.0180912 30.5576 0.0275072H18.5197L17.9259 2.06279H28.8758L28.2341 4.23029H17.2879L15.4844 10.4162H17.6604L18.8774 6.2621H28.7246C29.0407 6.25879 29.3462 6.15455 29.5913 5.96637C29.85 5.78355 30.0379 5.52564 30.1261 5.23228L31.3432 1.08168C31.3936 0.957937 31.4122 0.824675 31.3975 0.692833C31.3827 0.56099 31.335 0.434325 31.2583 0.323233Z"
        fill="#70706E"
      />
      <path
        d="M14.1966 9.72019C14.1379 9.91966 14.0118 10.0956 13.8374 10.2212C13.6629 10.3468 13.4498 10.4152 13.2303 10.416H1.9374C1.80538 10.4252 1.67302 10.403 1.5525 10.3513C1.43198 10.2996 1.32717 10.2202 1.24773 10.1203C1.17262 10.0146 1.12482 9.89375 1.10816 9.76747C1.09151 9.64119 1.10647 9.51299 1.15184 9.39315L3.90685 0.0273438H6.09757L3.63761 8.37724H12.4005L14.8605 0.0273438H17.0512L14.1966 9.72019Z"
        fill="#70706E"
      />
      <path
        d="M39.2256 0.0175781L41.9954 5.21539L36.1719 10.4132L39.2256 0.0175781Z"
        fill="#098041"
      />
      <path
        d="M37.284 0.0175781L40.0537 5.21539L34.2266 10.4132L37.284 0.0175781Z"
        fill="#E97626"
      />
    </g>
    <defs>
      <clipPath id="clip0_33821_37354">
        <rect width="42" height="14" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const PAYMENT_PARTNERS = [
  { id: "gpay", name: "Google Pay", icon: <GooglePayIcon /> },
  { id: "phonepe", name: "PhonePe", icon: <PhonePeIcon /> },
  { id: "applepay", name: "Apple Pay", icon: <ApplePayIcon /> },
  { id: "visa", name: "VISA", icon: <VisaIcon /> },
  { id: "upi", name: "UPI", icon: <UPIIcon /> },
];

// Rider ID → display name and premium field (for Summary add-ons; selection from user, premium from API)
const RIDER_ID_TO_SUMMARY = {
  "accidental-death": {
    name: "Accidental Death Benefits",
    premiumKey: "riderADBPremium",
  },
  "accidental-death-disability": {
    name: "Accidental Death and Disability",
    premiumKey: "riderADDPremium",
  },
  "accidental-death-disability-plus": {
    name: "Accidental Death and Disability Plus",
    premiumKey: "riderADDPlusPremium",
  },
  "critical-illness": {
    name: "Critical Illness Rider",
    premiumKey: "riderCIPremium",
  },
};

/**
 * Rider IDs: quote submit snapshot first (resume/summary), then nested quoteRiders,
 * then live Redux when the snapshot is missing (e.g. quote edited before re-submit).
 */
function resolveSelectedRiderIds(quoteData, quoteRidersRedux) {
  const fromQuote = quoteData?.selectedRiders;
  if (Array.isArray(fromQuote) && fromQuote.length > 0) return fromQuote;

  const fromQuoteRiders = quoteData?.quoteRiders?.selectedIds;
  if (Array.isArray(fromQuoteRiders) && fromQuoteRiders.length > 0) {
    return fromQuoteRiders;
  }

  const fromRedux = quoteRidersRedux?.selectedIds;
  if (Array.isArray(fromRedux) && fromRedux.length > 0) return fromRedux;

  return [];
}

/** Premium for one rider: quote submit snapshot first, then live premium, then per-rider maps. */
function resolveRiderPremiumAmount(id, meta, quoteData, premiumResult) {
  if (!meta) return null;
  const fromQuoteSnapshot = quoteData?.[meta.premiumKey];
  if (fromQuoteSnapshot != null) return Number(fromQuoteSnapshot);

  const fromLivePremium = premiumResult?.[meta.premiumKey];
  if (fromLivePremium != null) return Number(fromLivePremium);

  const fromApiMap = quoteData?.riderPremiumFromApi?.[id];
  if (fromApiMap != null) return Number(fromApiMap);

  const detail = Array.isArray(quoteData?.ridersDetail)
    ? quoteData.ridersDetail.find((r) => r?.riderId === id || r?.id === id)
    : null;
  if (detail?.premium != null) return Number(detail.premium);

  return null;
}

function buildSummarySelectedRiders(
  quoteData,
  premiumResult,
  quoteRidersRedux,
) {
  const ids = resolveSelectedRiderIds(quoteData, quoteRidersRedux);
  if (ids.length > 0) {
    return ids
      .map((id) => {
        const meta = RIDER_ID_TO_SUMMARY[id];
        if (!meta) return null;
        const premium = resolveRiderPremiumAmount(
          id,
          meta,
          quoteData,
          premiumResult,
        );
        if (premium == null) return null;
        return { name: meta.name, premium };
      })
      .filter(Boolean);
  }
  if (quoteData?.riderName) {
    return [
      {
        name: quoteData.riderName,
        premium: Number(quoteData.riderPremiumAmount) || 50,
      },
    ];
  }
  return [];
}

/** First whole number in a tenure string (e.g. "37 Years", "15") — used for PPT fields from API/quote. */
function parseYearCount(val) {
  if (val == null) return null;
  const s = String(val).trim();
  if (!s) return null;
  const m = s.replace(/,/g, "").match(/\d+/);
  if (!m) return null;
  const n = Number(m[0]);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function finiteNonNegativeInt(val) {
  if (val == null || val === "") return null;
  const x = Number(val);
  if (!Number.isFinite(x) || x < 0) return null;
  return Math.floor(x);
}

/**
 * Current life-assured age for display math. Uses quote snapshot, lead, DOB, or coverTillAge − policyTerm.
 * Returns 0 only when nothing reliable is available.
 */
function resolveLifeAssuredAgeForSummary(
  quoteData,
  leadInput,
  leadResult,
  leadFormData,
) {
  const fromExplicit = [
    quoteData?.lifeAssuredAge,
    leadInput?.lifeAssuredAge,
    leadResult?.lifeAssuredAge,
  ];
  for (const c of fromExplicit) {
    const a = finiteNonNegativeInt(c);
    if (a != null) return a;
  }
  const dobCandidates = [
    getLeadDateOfBirth(leadFormData, null, quoteData),
    quoteData?.lifeAssuredDob,
    quoteData?.leadInput?.lifeAssuredDob,
    quoteData?.leadInput?.dateOfBirth,
    leadInput?.lifeAssuredDob,
    leadInput?.dateOfBirth,
    leadResult?.lifeAssuredDob,
    leadResult?.dateOfBirth,
  ];
  for (const dob of dobCandidates) {
    const a = calculateAge(dob);
    if (a != null && a >= 0) return a;
  }
  const cta = finiteNonNegativeInt(quoteData?.coverTillAge);
  const pt = finiteNonNegativeInt(quoteData?.policyTerm);
  if (cta != null && pt != null && cta >= pt) return cta - pt;
  return 0;
}

/**
 * Premium paying term in years (PPT). Prefer parsed tenure strings; else limited-pay selection; else policy term.
 */
function resolvePremiumPayingTermYears(quoteData, paymentTermType) {
  const fromStrings =
    parseYearCount(quoteData?.paymentTermTenure) ??
    parseYearCount(quoteData?.premiumPayingTerm);
  if (fromStrings != null && fromStrings > 0) return fromStrings;
  if (paymentTermType === "Limited Pay") {
    const lp = finiteNonNegativeInt(quoteData?.selectedLimitedPayTerm);
    if (lp != null && lp > 0) return lp;
  }
  const pt = finiteNonNegativeInt(quoteData?.policyTerm);
  if (pt != null && pt > 0) return pt;
  return 0;
}

/**
 * Installment total including riders. `finalDiscountedPremium` / first BI modal line are base-only;
 * prefer persisted quote totals after refresh.
 */
function resolveTotalPayableAmount(
  quoteData,
  premiumResult,
  biModalPremium,
  selectedRiders,
) {
  const explicit =
    quoteData?.totalPayableAmount ??
    quoteData?.premiumAmount ??
    quoteData?.quote?.premiumAmount ??
    quoteData?.quoteSummaryData?.totalPayableAmount ??
    premiumResult?.totalPayableAmount;
  if (explicit != null && explicit !== "") {
    const n = Number(explicit);
    if (Number.isFinite(n) && n > 0) return n;
  }

  const base = Number(
    premiumResult?.finalDiscountedPremium ?? biModalPremium ?? 0,
  );
  const riderSum = (selectedRiders || []).reduce(
    (sum, r) => sum + (Number(r?.premium) || 0),
    0,
  );
  if (base > 0 && riderSum > 0) return base + riderSum;
  return base > 0 ? base : 0;
}

const TermInsuranceJourneySummary = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const store = useStore();
  const { isResuming } = usePaymentReturnResume();
  const {
    quoteResult,
    premiumResult,
    effectiveLeadResult: leadResult,
    applicationNumber,
    caseId,
    handleBackToQuote: onBack,
    isBackToQuoteLoading,
    handlePayment: onPayment,
    handleSkipPayment: onSkipPayment,
  } = useJourney();

  const [benefitDeclaration, setBenefitDeclaration] = useState(false);
  const [termsAgreement, setTermsAgreement] = useState(false);
  const [describedPrivacyPolicy, setDescribedPrivacyPolicy] = useState(false);
  const [autoRenewal, setAutoRenewal] = useState(true);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPaymentAwaitingModal, setShowPaymentAwaitingModal] =
    useState(false);
  const [paymentAwaitingMessage, setPaymentAwaitingMessage] = useState("");
  const [error, setError] = useState(null);
  const [isPdfDwnld, setIsPdfDwnld] = useState(false);
  const [biGuidedHintCoords, setBiGuidedHintCoords] = useState(null);
  const [biGuidedHintPlaceAbove, setBiGuidedHintPlaceAbove] = useState(false);
  const showBiDownloadGuidedHint = !isPdfDwnld;
  const paymentAwaitingDisplayMessage =
    paymentAwaitingMessage ||
    "We're verifying your payment. If this takes a bit longer, you can still continue via 'Retrieve Application'";
  const { declarations } = useSelector((s) => s.journey);
  const leadFormData = useSelector((s) => s.journey.leadFormData);
  const journeyReduxSnapshot = useSelector((s) => ({
    quoteRiders: s.journey.quoteRiders,
    quoteOptions: s.journey.quoteOptions,
    multiDimensionByPlan: s.journey.multiDimensionByPlan,
    premiumResult: s.journey.premiumResult,
    multiDimensionPremiumResult: s.journey.multiDimensionPremiumResult,
    quoteResult: s.journey.quoteResult,
    leadFormData: s.journey.leadFormData,
    declarations: s.journey.declarations,
  }));

  useEffect(() => {
    if (declarations) {
      setBenefitDeclaration(!!declarations.benefitDeclaration);
      setTermsAgreement(!!declarations.termsAgreement);
      setDescribedPrivacyPolicy(!!declarations.describedPrivacyPolicy);
      setAutoRenewal(
        declarations.autoRenewal !== undefined
          ? declarations.autoRenewal
          : true,
      );
    }
  }, [declarations]);

  useEffect(() => {
    if (!isPdfDwnld) {
      setError(
        "Please review the Benefit Illustration before making the payment",
      );
    }
  }, [isPdfDwnld]);

  useLayoutEffect(() => {
    if (!showBiDownloadGuidedHint) {
      setBiGuidedHintCoords(null);
      return undefined;
    }

    const updateHintPosition = () => {
      const target = document.querySelector(".summary-bi-download-guided-target");
      if (!target) {
        setBiGuidedHintCoords(null);
        return;
      }
      const rect = target.getBoundingClientRect();
      const gap = 14;
      const hintEstimatedHeight = 88;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const placeAbove =
        spaceBelow < hintEstimatedHeight + gap && spaceAbove > spaceBelow;
      const left = rect.left + rect.width / 2;
      const top = placeAbove ? rect.top - gap : rect.bottom + gap;

      setBiGuidedHintPlaceAbove(placeAbove);
      setBiGuidedHintCoords({ top, left });
    };

    const target = document.querySelector(".summary-bi-download-guided-target");
    target?.scrollIntoView({ behavior: "smooth", block: "center" });

    const rafId = requestAnimationFrame(updateHintPosition);
    const timeoutId = window.setTimeout(updateHintPosition, 360);

    window.addEventListener("resize", updateHintPosition);
    window.addEventListener("scroll", updateHintPosition, true);

    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      window.removeEventListener("resize", updateHintPosition);
      window.removeEventListener("scroll", updateHintPosition, true);
    };
  }, [showBiDownloadGuidedHint]);

  const leadInput = leadResult?.leadInput || leadResult || {};
  const quoteData = quoteResult || {};

  const planName = quoteData.productName || "Super Suraksha Elite";
  const productCode =
    quoteData.productCode || leadResult?.assignedProductCode || "";
  const bisOpsProductCode =
    quoteData.bisOpsProductCode ?? quoteResult?.bisOpsProductCode ?? "";
  // Resolve to BI API product ID (22062 Elite or 22063 Plus) for payment; only this id is sent, not bisOpsProductCode.
  const biApiProductId = resolveBiApiProductId(
    planName || productCode || bisOpsProductCode,
  );
  useEffect(() => {
    if (biApiProductId) {
      dispatch(journeyActions.setBiApiProductId(biApiProductId));
    }
  }, [biApiProductId, dispatch]);

  const coverAmount =
    quoteData.coverAmount || leadResult?.maxCoverAmount || 6000000;
  const coverTillAge = quoteData.coverTillAge || 55;
  const policyTerm = quoteData.policyTerm || 25;
  const paymentTermType = quoteData.paymentTermType || "Regular Pay";
  const premiumFrequency =
    quoteData.premiumPaymentFrequency ||
    quoteData.premiumFrequency ||
    premiumResult?.premiumFrequency ||
    "Monthly";

  const biResult = quoteResult?.biResult || {};
  const pdfUrl =
    quoteData?.pdfUrl ||
    biResult?.response?.PDFURL ||
    biResult?.PDFURL ||
    biResult?.pdfUrl ||
    biResult?.pdfURL ||
    quoteResult?.PDFURL ||
    quoteResult?.pdfUrl ||
    quoteResult?.pdfURL ||
    quoteResult?.biPdfUrl ||
    null;

  const biModalPremium =
    biResult?.totalModalPremium ??
    biResult?.InputValidationStatus?.[0]?.ModalPremium ??
    biResult?.InputValidationStatus?.[0]?.modalPremium;

  const quoteRidersRedux = journeyReduxSnapshot.quoteRiders;

  // Show only riders the user selected on Quote; prefer quote snapshot premiums over stale premiumResult
  const selectedRiders = useMemo(
    () =>
      buildSummarySelectedRiders(quoteData, premiumResult, quoteRidersRedux),
    [quoteData, premiumResult, quoteRidersRedux],
  );

  const totalPayableAmount = useMemo(
    () =>
      resolveTotalPayableAmount(
        quoteData,
        premiumResult,
        biModalPremium,
        selectedRiders,
      ),
    [quoteData, premiumResult, biModalPremium, selectedRiders],
  );
  const premiumSavings = premiumResult?.premiumSavingsAmount || 0;
  const onlineDiscount = premiumResult?.onlineDiscount || "10%";
  const salariedDiscount = premiumResult?.salariedDiscount || "5%";

  const payTillAge = useMemo(() => {
    const la = resolveLifeAssuredAgeForSummary(
      quoteData,
      leadInput,
      leadResult,
      leadFormData,
    );
    const ppt = resolvePremiumPayingTermYears(quoteData, paymentTermType);
    return la + ppt;
  }, [quoteData, leadInput, leadResult, leadFormData, paymentTermType]);

  const canProceed =
    benefitDeclaration && termsAgreement && describedPrivacyPolicy;

  const customerName = useMemo(() => {
    return (
      [
        leadInput.proposerFirstName ||
          leadInput.firstName ||
          leadInput.lifeAssuredFirstName,
        leadInput.proposerMiddleName ||
          leadInput.middleName ||
          leadInput.lifeAssuredMiddleName,
        leadInput.proposerLastName ||
          leadInput.lastName ||
          leadInput.lifeAssuredLastName,
      ]
        .filter(Boolean)
        .join(" ") ||
      leadInput.lifeAssuredName ||
      leadInput.customerName ||
      leadInput.fullName ||
      leadInput.name ||
      "Customer"
    );
  }, [leadInput]);

  const customerMobile = useMemo(() => {
    return (
      leadInput.proposerMobileNumber ||
      leadInput.mobileNumber ||
      leadInput.lifeAssuredMobileNumber ||
      leadInput.mobile ||
      leadInput.phone ||
      leadInput.contactNumber ||
      ""
    );
  }, [leadInput]);

  const customerEmail = useMemo(() => {
    return (
      leadInput.proposerEmail ||
      leadInput.emailAddress ||
      leadInput.email ||
      leadInput.lifeAssuredEmail ||
      leadInput.emailId ||
      ""
    );
  }, [leadInput]);

  const buildQuoteSummaryData = useCallback(
    () => ({
      planName,
      coverAmount,
      coverAmountFormatted: formatCurrency(coverAmount),
      coverTillAge,
      policyTerm,
      paymentTermType,
      premiumFrequency,
      totalPayableAmount,
      totalPayableAmountFormatted: formatCurrency(totalPayableAmount),
      premiumSavings,
      selectedRiders: selectedRiders.map((r) => ({
        name: r.name,
        premium: r.premium,
      })),
      hasAddOns: selectedRiders.length > 0,
      applicationNumber,
      timestamp: new Date().toISOString(),
      // Declaration checkboxes – persisted in quote summary JSON and journey
      declarations: {
        benefitDeclarationAccepted: benefitDeclaration,
        termsAgreementAccepted: termsAgreement,
        describedPrivacyPolicyAccepted: describedPrivacyPolicy,
        autoRenewal,
      },
    }),
    [
      planName,
      coverAmount,
      coverTillAge,
      policyTerm,
      paymentTermType,
      premiumFrequency,
      totalPayableAmount,
      premiumSavings,
      selectedRiders,
      applicationNumber,
      benefitDeclaration,
      termsAgreement,
      autoRenewal,
    ],
  );

  const persistQuoteSummary = useCallback(
    async (extraData = {}) => {
      const quoteSummaryData = {
        ...buildQuoteSummaryData(),
        ...extraData,
      };
      await updateQuoteSummary(applicationNumber, quoteSummaryData);
      return quoteSummaryData;
    },
    [applicationNumber, buildQuoteSummaryData],
  );

  const handleSkipPayment = useCallback(async () => {
    if (!canProceed) {
      setError("Please accept the declarations to continue.");
      return;
    }
    setIsProcessing(true);
    setError(null);
    try {
      const quoteSummaryData = await persistQuoteSummary({
        skippedPayment: true,
      });

      await updateJourneyStage(
        applicationNumber,
        caseId,
        JOURNEY_STAGE.QUOTE_SUMMARY,
        {
          summaryData: {
            benefitDeclaration,
            termsAgreement,
            describedPrivacyPolicy,
            autoRenewal,
            skippedPayment: true,
            paymentFlowMode: PAYMENT_FLOW.PAY_LAST,
            quoteSummaryPersisted: true,
          },
          quoteSummaryData,
          journeyReduxSnapshot: slimJourneyReduxSnapshotForPersistence(
            cloneForJourney(journeyReduxSnapshot),
          ),
        },
      );
      onSkipPayment?.();
    } catch (err) {
      logError("[Summary] Failed to save summary data on skip payment", err);
      setError(
        err?.message || "Failed to save quote summary. Please try again.",
      );
    } finally {
      setIsProcessing(false);
    }
  }, [
    canProceed,
    applicationNumber,
    caseId,
    benefitDeclaration,
    termsAgreement,
    autoRenewal,
    onSkipPayment,
    persistQuoteSummary,
    journeyReduxSnapshot,
  ]);

  const handlePayment = useCallback(async () => {
    if (!canProceed) {
      setError("Please accept the declarations to continue.");
      return;
    }
    if (!biApiProductId) {
      setError(
        "Unable to determine product for payment. Please ensure a valid plan (Elite or Plus) is selected.",
      );
      return;
    }
    setIsProcessing(true);
    setPaymentAwaitingMessage("");
    setShowPaymentAwaitingModal(false);
    setError(null);
    try {
      const redirected = await redirectToLatestJourneyStage({
        applicationNumber,
        dispatch,
        navigate,
        getState: store.getState,
        currentPath: "/summary",
      });
      if (redirected) return;

      const quoteSummaryData = await persistQuoteSummary({
        proceedToPayment: true,
      });
      await saveSummaryStageData(applicationNumber, caseId, {
        ...quoteSummaryData,
        benefitDeclaration,
        termsAgreement,
        describedPrivacyPolicy,
        autoRenewal,
        proceedToPayment: true,
      });

      const paymentData = {
        applicationNumber,
        customerName,
        premiumAmount: totalPayableAmount,
        mobileNumber: customerMobile,
        emailAddress: customerEmail,
        productId: biApiProductId || undefined,
        paymentFlowMode: PAYMENT_FLOW.PAY_FIRST,
        transactionStatus: "INITIATED",
      };

      await updateJourneyStage(
        applicationNumber,
        caseId,
        JOURNEY_STAGE.PAYMENT_GATEWAY_REDIRECTION,
        {
          paymentData,
          quoteSummaryData,
          journeyReduxSnapshot: slimJourneyReduxSnapshotForPersistence(
            cloneForJourney(journeyReduxSnapshot),
          ),
        },
      );

      const paymentResult = onPayment ? await onPayment(paymentData) : null;
      if (paymentResult?.paymentAwaiting) {
        setPaymentAwaitingMessage(
          paymentResult.message || paymentAwaitingDisplayMessage,
        );
        setShowPaymentAwaitingModal(true);
      }
    } catch (err) {
      logError("[Summary] Payment failed", err);
      setError(err.message || "Failed to process. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  }, [
    canProceed,
    applicationNumber,
    caseId,
    benefitDeclaration,
    termsAgreement,
    autoRenewal,
    onPayment,
    paymentAwaitingDisplayMessage,
    customerName,
    customerMobile,
    customerEmail,
    totalPayableAmount,
    biApiProductId,
    persistQuoteSummary,
    journeyReduxSnapshot,
    dispatch,
    navigate,
    store,
  ]);

  const handleDownloadBI = useCallback(() => {
    if (pdfUrl) {
      setIsPdfDwnld(true);
      setError("");
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
    } else {
      setError(
        "Benefit Illustration is not available. Please try again later.",
      );
    }
  }, [pdfUrl]);

  if (isResuming || isProcessing || isBackToQuoteLoading) {
    return (
      <div
        className="quote-premium-loader-overlay"
        aria-live="polite"
        aria-busy="true"
      >
        <BrandLoader color="#97262A" loading={true} size={15} />
      </div>
    );
  }

  return (
    <div className="term-insurance-journey-summary">
      {showBiDownloadGuidedHint && biGuidedHintCoords && (
        <div
          className={`quote-guided-hint ${
            biGuidedHintPlaceAbove
              ? "quote-guided-hint--above"
              : "quote-guided-hint--below"
          }`}
          style={{
            top: biGuidedHintCoords.top,
            left: biGuidedHintCoords.left,
          }}
        >
          {!biGuidedHintPlaceAbove && <GuidedHintArrow direction="up" />}
          <p className="quote-guided-hint__text">{SUMMARY_BI_GUIDED_HINT_TEXT}</p>
          {biGuidedHintPlaceAbove && <GuidedHintArrow direction="down" />}
        </div>
      )}
      <header className="sumarry-header">
        <div className="sumarry-header-wraper">
          <div className="header-left">
            <button
              className="back-button"
              onClick={onBack}
              disabled={isBackToQuoteLoading}
              aria-label="Go back"
              aria-busy={isBackToQuoteLoading}
            >
              <svg
                width="18"
                height="14"
                viewBox="0 0 18 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17 7H1M1 7L7 13M1 7L7 1"
                  stroke="#252527"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <h6 className="header-title m-0">Review before Checkout</h6>
          </div>
          <button
            className="help-button btn secondary-button regular"
            onClick={() => setShowHelpModal(true)}
          >
            <svg
              width="20"
              height="21"
              viewBox="0 0 20 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.6772 2.87806C15.3151 1.50799 13.5769 0.573101 11.683 0.191867C9.789 -0.189367 7.82449 0.000214264 6.03838 0.736587C4.25227 1.47296 2.72496 2.72298 1.64998 4.32823C0.575006 5.93349 0.000755732 7.82174 0 9.75369V15.0037C0 15.6004 0.237053 16.1727 0.65901 16.5947C1.08097 17.0166 1.65326 17.2537 2.25 17.2537H3.75C4.34674 17.2537 4.91903 17.0166 5.34099 16.5947C5.76295 16.1727 6 15.6004 6 15.0037V11.2537C6 10.657 5.76295 10.0847 5.34099 9.6627C4.91903 9.24074 4.34674 9.00369 3.75 9.00369H1.53375C1.67839 7.43988 2.26578 5.94969 3.22709 4.70779C4.18839 3.46588 5.48377 2.52372 6.96141 1.99172C8.43904 1.45972 10.0377 1.35993 11.57 1.70404C13.1024 2.04815 14.5048 2.82191 15.6131 3.93463C16.9677 5.29616 17.8009 7.09031 17.9672 9.00369H15.75C15.1533 9.00369 14.581 9.24074 14.159 9.6627C13.7371 10.0847 13.5 10.657 13.5 11.2537V15.0037C13.5 15.6004 13.7371 16.1727 14.159 16.5947C14.581 17.0166 15.1533 17.2537 15.75 17.2537H18C18 17.8504 17.7629 18.4227 17.341 18.8447C16.919 19.2666 16.3467 19.5037 15.75 19.5037H10.5C10.3011 19.5037 10.1103 19.5827 9.96967 19.7234C9.82902 19.864 9.75 20.0548 9.75 20.2537C9.75 20.4526 9.82902 20.6434 9.96967 20.784C10.1103 20.9247 10.3011 21.0037 10.5 21.0037H15.75C16.7446 21.0037 17.6984 20.6086 18.4017 19.9053C19.1049 19.2021 19.5 18.2483 19.5 17.2537V9.75369C19.5049 8.47829 19.2581 7.21446 18.7737 6.03462C18.2893 4.85477 17.5768 3.7821 16.6772 2.87806ZM3.75 10.5037C3.94891 10.5037 4.13968 10.5827 4.28033 10.7234C4.42098 10.864 4.5 11.0548 4.5 11.2537V15.0037C4.5 15.2026 4.42098 15.3934 4.28033 15.534C4.13968 15.6747 3.94891 15.7537 3.75 15.7537H2.25C2.05109 15.7537 1.86032 15.6747 1.71967 15.534C1.57902 15.3934 1.5 15.2026 1.5 15.0037V10.5037H3.75ZM15.75 15.7537C15.5511 15.7537 15.3603 15.6747 15.2197 15.534C15.079 15.3934 15 15.2026 15 15.0037V11.2537C15 11.0548 15.079 10.864 15.2197 10.7234C15.3603 10.5827 15.5511 10.5037 15.75 10.5037H18V15.7537H15.75Z"
                fill="#97262A"
              />
            </svg>
            Need Help?
          </button>
        </div>
      </header>

      <div className="summary-content">
        <div className="summary-wraper">
          <div className="summary-left-panel">
            <div className="summary-plan-header">
              <div className="application-banner">
                <span className="application-label">Application Number: </span>
                <span className="application-number">
                  {applicationNumber || "INS0122462"}
                </span>
              </div>

              <div className="plan-card"> 
                <div className="plan-card-header">
                  <div className="plan-badge">
                    <span>Your Plan</span>
                    <h6 className="plan-name p-0">{planName}</h6>
                  </div>
                  <div className={`quote-guided-target ${
                        showBiDownloadGuidedHint
                          ? "quote-guided-target--active summary-bi-download-guided-target"
                          : ""
                      }`}>
                    <button
                      className={`bi-download-btn ${!pdfUrl ? "disabled" : ""} `}
                      onClick={handleDownloadBI}
                      disabled={!pdfUrl}
                      title={
                        pdfUrl
                          ? "Download Benefits Illustration PDF"
                          : "PDF not available"
                      }
                    >
                      Benefits Illustration
                      <svg
                        width="21"
                        height="21"
                        viewBox="0 0 21 21"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M19.0293 19.0273L1.0293 19.0273M16.0293 9.02734L10.0293 15.0273M10.0293 15.0273L4.0293 9.02734M10.0293 15.0273L10.0293 1.02734"
                          stroke="#97262A"
                          strokeWidth="2.05714"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="plan-details-wraper">
              <div className="plan-details-card">
                <div className="plan-details-header">
                  Here is your Plan Details
                </div>
                <div className="plan-details-grid">
                  <div className="plan-detail-item">
                    <span className="detail-label">Cover Amount</span>
                    <span className="detail-value">
                      {formatCurrency(coverAmount)}
                    </span>
                  </div>
                  <div className="divider"></div>
                  <div className="plan-detail-item">
                    <span className="detail-label">Cover Till Age</span>
                    <span className="detail-value">{coverTillAge} years</span>
                    <span className="detail-note">
                      You&apos;ll Pay till age {payTillAge}!
                    </span>
                  </div>
                  <div className="divider"></div>
                  <div className="plan-detail-item">
                    <span className="detail-label">Payment Term</span>
                    <span className="detail-value">{paymentTermType}</span>
                  </div>
                  <div className="divider"></div>
                  <div className="plan-detail-item">
                    <span className="detail-label">Premium Frequency</span>
                    <span className="detail-value">{premiumFrequency}</span>
                  </div>
                </div>
              </div>

              <div className="declarations-section">
                <label className="declaration-item checkbox">
                  <input
                    type="checkbox"
                    checked={benefitDeclaration}
                    onChange={(e) => {
                      const value = e.target.checked;
                      setBenefitDeclaration(value);
                      dispatch(
                        journeyActions.setDeclarations({
                          benefitDeclaration: value,
                        }),
                      );
                    }}
                  />
                  <span className="declaration-text">
                    {TEXT_CONTENT.DECLARATION_TEXT}
                  </span>
                </label>
                <label className="declaration-item checkbox">
                  <input
                    type="checkbox"
                    checked={termsAgreement}
                    onChange={(e) => {
                      const value = e.target.checked;
                      setTermsAgreement(value);
                      dispatch(
                        journeyActions.setDeclarations({
                          termsAgreement: value,
                        }),
                      );
                    }}
                  />
                  <span className="declaration-text">
                    {TEXT_CONTENT.DECLARATION_TEXTT}
                  </span>
                </label>
                <label className="declaration-item checkbox">
                  <input
                    type="checkbox"
                    checked={describedPrivacyPolicy}
                    onChange={(e) => {
                      const value = e.target.checked;
                      setDescribedPrivacyPolicy(value);
                      dispatch(
                        journeyActions.setDeclarations({
                          describedPrivacyPolicy: value,
                        }),
                      );
                    }}
                  />
                  <span className="declaration-text">
                    {TEXT_CONTENT.DECLARATION_TEXTTT}
                  </span>
                </label>
              </div>
            </div>
          </div>

          <aside className="summary-right-panel">
            <div className="premium-amount-section">
              <div className="premium-main">
                {formatCurrency(totalPayableAmount)}{" "}
                <span className="premium-period">
                  /{" "}
                  {premiumFrequency === "Yearly"
                    ? "Year"
                    : premiumFrequency === "Half-yearly"
                      ? "Half-year"
                      : premiumFrequency === "Quarterly"
                        ? "Quarter"
                        : "Month"}
                </span>
              </div>
              <div className="premium-label">Total Payable Amount</div>
              {formatCurrency(premiumSavings) !== "₹ 0" && (
                <div className="premium-savings">
                  You save <span>{formatCurrency(premiumSavings)}</span> on this
                  plan
                </div>
              )}
            </div>

            <div className="premium-summary-breakdown">
              <h6 className="premium-summary-breakdown-title large-body semibold">
                Premium Breakdown
              </h6>
              <div className="premium-summary-row-plan">
                <div className="premium-summary-row-label">Plan Details</div>
                <div className="premium-summary-row-plan-text">
                  IndusInd Nippon Life {planName}
                </div>

                <div className="premium-summary-row-both">
                  <div className="premium-summary-row-term">
                    <div className="premium-summary-row-label">Term:</div>
                    <div className="premium-summary-row-value">
                      {policyTerm} years
                    </div>
                  </div>
                  <span className="divider">|</span>
                  <div className="premium-summary-row-term">
                    <div className="premium-summary-row-label">Cover:</div>
                    <div className="premium-summary-row-value">
                      {formatCurrency(coverAmount)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="premium-summary-addons">
                <div className="premium-summary-addons-header">Add Ons</div>
                <div className="premium-summary-addonsPlans">
                  {selectedRiders.length > 0 ? (
                    selectedRiders.map((rider, index) => (
                      <div key={index} className="premium-summary-addon-row">
                        <div className="premium-summary-addon-main">
                          <span className="premium-summary-addon-name">
                            {rider.name}
                          </span>
                        </div>
                        <div className="premium-summary-addon-right">
                          <span className="premium-summary-addon-price">
                            {formatCurrency(rider.premium)} /{" "}
                            {getDisplayFrequencyLabel(premiumFrequency)}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="premium-summary-addon-row--empty">
                      No add-ons selected
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="auto-bottom-section">
              <div className="auto-renewal-section">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    // checked={false}
                    // disabled={true}
                    // onChange={(e) => {
                    //   const value = e.target.checked;
                    //   setAutoRenewal(value);
                    //   dispatch(
                    //     journeyActions.setDeclarations({
                    //       autoRenewal: value,
                    //     }),
                    //   );

                    // }}
                  />
                  <div className="auto-renewal-content">
                    <span className="auto-renewal-title">
                      Pay with Auto-Renewal
                    </span>
                    <span className="auto-renewal-subtitle">
                      (Stay Protected Without Any Breaks)
                    </span>
                  </div>
                </label>
                <div className="auto-renewal-description">
                  {TEXT_CONTENT.AUTO_RENEWAL}
                </div>
              </div>
              <div className="features-section">
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg
                      width="23"
                      height="21"
                      viewBox="0 0 23 21"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4.32227 6.65158C4.32227 5.88989 4.32227 4.66187 4.44836 4.3345C4.55975 4.0453 4.74075 3.78725 4.97573 3.58266C5.24171 3.35107 5.60265 3.21735 6.32452 2.94991L11.4948 1.03436C11.6953 0.960089 11.7955 0.922952 11.8986 0.908231C11.9901 0.895173 12.083 0.895173 12.1745 0.908231C12.2776 0.922952 12.3778 0.960089 12.5783 1.03436L17.7486 2.94991C18.4705 3.21735 18.8314 3.35107 19.0974 3.58266C19.3324 3.78725 19.5134 4.0453 19.6247 4.3345C19.7508 4.66187 19.7508 5.04272 19.7508 5.8044V10.3605C19.7508 15.0368 14.5881 18.4378 12.7096 19.5205C12.4961 19.6436 12.3894 19.7051 12.2387 19.737C12.1218 19.7618 11.9513 19.7618 11.8344 19.737C11.6837 19.7051 11.577 19.6436 11.3635 19.5205C10.7033 19.14 9.63754 18.4732 8.53275 17.5653"
                        stroke="#96272A"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M19.7559 12.8984L21.4701 12.8984"
                        stroke="#96272A"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M0.900391 13.4408H1.64892L3.13794 9.46875L5.39589 17.183L7.62838 13.4408H8.35406"
                        stroke="#96272A"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M12.0449 5.50391V12.361"
                        stroke="#96272A"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M8.61133 8.93359L15.4685 8.93359"
                        stroke="#96272A"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="feature-text">
                    Flexible
                    <br />
                    EMI Options
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg
                      width="23"
                      height="21"
                      viewBox="0 0 23 21"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4.32227 6.65158C4.32227 5.88989 4.32227 4.66187 4.44836 4.3345C4.55975 4.0453 4.74075 3.78725 4.97573 3.58266C5.24171 3.35107 5.60265 3.21735 6.32452 2.94991L11.4948 1.03436C11.6953 0.960089 11.7955 0.922952 11.8986 0.908231C11.9901 0.895173 12.083 0.895173 12.1745 0.908231C12.2776 0.922952 12.3778 0.960089 12.5783 1.03436L17.7486 2.94991C18.4705 3.21735 18.8314 3.35107 19.0974 3.58266C19.3324 3.78725 19.5134 4.0453 19.6247 4.3345C19.7508 4.66187 19.7508 5.04272 19.7508 5.8044V10.3605C19.7508 15.0368 14.5881 18.4378 12.7096 19.5205C12.4961 19.6436 12.3894 19.7051 12.2387 19.737C12.1218 19.7618 11.9513 19.7618 11.8344 19.737C11.6837 19.7051 11.577 19.6436 11.3635 19.5205C10.7033 19.14 9.63754 18.4732 8.53275 17.5653"
                        stroke="#96272A"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M19.7559 12.8984L21.4701 12.8984"
                        stroke="#96272A"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M0.900391 13.4408H1.64892L3.13794 9.46875L5.39589 17.183L7.62838 13.4408H8.35406"
                        stroke="#96272A"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M12.0449 5.50391V12.361"
                        stroke="#96272A"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M8.61133 8.93359L15.4685 8.93359"
                        stroke="#96272A"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="feature-text">
                    Secure
                    <br />
                    Auto-Debit Payments
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg
                      width="23"
                      height="21"
                      viewBox="0 0 23 21"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4.32227 6.65158C4.32227 5.88989 4.32227 4.66187 4.44836 4.3345C4.55975 4.0453 4.74075 3.78725 4.97573 3.58266C5.24171 3.35107 5.60265 3.21735 6.32452 2.94991L11.4948 1.03436C11.6953 0.960089 11.7955 0.922952 11.8986 0.908231C11.9901 0.895173 12.083 0.895173 12.1745 0.908231C12.2776 0.922952 12.3778 0.960089 12.5783 1.03436L17.7486 2.94991C18.4705 3.21735 18.8314 3.35107 19.0974 3.58266C19.3324 3.78725 19.5134 4.0453 19.6247 4.3345C19.7508 4.66187 19.7508 5.04272 19.7508 5.8044V10.3605C19.7508 15.0368 14.5881 18.4378 12.7096 19.5205C12.4961 19.6436 12.3894 19.7051 12.2387 19.737C12.1218 19.7618 11.9513 19.7618 11.8344 19.737C11.6837 19.7051 11.577 19.6436 11.3635 19.5205C10.7033 19.14 9.63754 18.4732 8.53275 17.5653"
                        stroke="#96272A"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M19.7559 12.8984L21.4701 12.8984"
                        stroke="#96272A"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M0.900391 13.4408H1.64892L3.13794 9.46875L5.39589 17.183L7.62838 13.4408H8.35406"
                        stroke="#96272A"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M12.0449 5.50391V12.361"
                        stroke="#96272A"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M8.61133 8.93359L15.4685 8.93359"
                        stroke="#96272A"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="feature-text">
                    0% Interest
                    <br />
                    on Renewals
                  </div>
                </div>
              </div>

              <div className="payment-partners-section">
                <span className="partners-label">Payment Partners:</span>
                <div className="partners-icons">
                  {PAYMENT_PARTNERS.map((partner) => (
                    <span
                      key={partner.id}
                      className="partner-icon"
                      title={partner.name}
                    >
                      {partner.text || partner.icon}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="quote-submit-summary">
        {error && (
          <div
            className="error-message error-message-inline"
            role="alert"
            aria-live="assertive"
          >
            {error}
          </div>
        )}
        <div className="bottom-banner">
          {onlineDiscount} Online and {salariedDiscount} salaried discount
          applied on first year premium
        </div>

        <div className="quote-submit-strip">
          {API_BASE_URL.includes("localhost") && (
            <div className="quote-submit-left ">
              <button
                className="btn-skip-payment btn-submit-quote btn secondary-button regular"
                onClick={handleSkipPayment}
                disabled={isProcessing || !canProceed}
              >
                Skip Payment for Now
              </button>
            </div>
          )}

          <div className="">
            <button
              className="btn-pay btn-submit-quote btn primary-button regular"
              onClick={handlePayment}
              disabled={isProcessing || !canProceed || !isPdfDwnld}
            >
              {isProcessing
                ? "Processing..."
                : `Pay ${formatCurrency(totalPayableAmount)}`}
            </button>
          </div>
        </div>
      </section>

      <NeedhelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title="Reach us through your preferred mode"
        type="success"
      />
      <Modal
        isOpen={showPaymentAwaitingModal}
        onClose={() => setShowPaymentAwaitingModal(false)}
        title="Payment Confirmation Pending"
        btnTitle="Okay"
        type="success"
      >
        <p>{paymentAwaitingDisplayMessage}</p>
      </Modal>
    </div>
  );
};

export default TermInsuranceJourneySummary;
